import { useEffect, useRef, useState } from 'react'

import screenfull from 'screenfull'

import { RectI } from '../../types/types'
import { getDeviceClientRect, getTailgateClientRect } from '../../utils/chart'
import { isPopupOpen } from '../utils/utils'

export type PopoverElementKind = 'device' | 'tailgate'

export interface UseHoverCardPositionIProps {
  elementId: string
  isHovering: boolean
  kind?: PopoverElementKind
  /**
   * Bump this to force the card fully closed, bypassing the
   * isContentHovered grace below. Pan/zoom, a floor switch, panel
   * collapse, and modal open/close can invalidate the anchor even while
   * the pointer is still resting on the card, so those must close it
   * unconditionally rather than respecting the "moving onto the card"
   * exception meant only for ordinary marker-to-card pointer travel.
   */
  forceCloseSignal?: number
  /** Bump this to open the card immediately for an explicit marker click. */
  forceOpenSignal?: number
}

export interface UseHoverCardPositionResultIProps {
  triggerPosition: RectI
  isOpen: boolean
  onContentMouseEnter: () => void
  onContentMouseLeave: () => void
  onPopoverClose: () => void
}

const INIT_POSITION: RectI = { x: 0, y: 0, width: 0, height: 0 }

/** Hover dwell before the card opens. */
const HOVER_OPEN_DELAY_MS = 1200

/** Grace period before closing, so the pointer can travel from the marker to the card. */
const CLOSE_GRACE_MS = 200

const getClientRectByKind = (elementId: string, kind?: PopoverElementKind) => {
  switch (kind) {
    case 'device':
      return getDeviceClientRect(elementId)
    case 'tailgate':
      return getTailgateClientRect(elementId)
  }
}

/**
 * Drives a floor plan hover card's open/close position, shared by the
 * legacy and dark popovers so the anchoring and grace-period logic can't
 * drift between the two.
 */
export const useHoverCardPosition = ({
  elementId,
  isHovering,
  kind,
  forceCloseSignal = 0,
  forceOpenSignal = 0,
}: UseHoverCardPositionIProps): UseHoverCardPositionResultIProps => {
  const openTimeoutRef = useRef(null)
  const closeTimeoutRef = useRef(null)
  const lastForceOpenSignalRef = useRef(0)
  const elementIdRef = useRef(elementId)
  elementIdRef.current = elementId

  const [triggerPosition, setTriggerPosition] = useState(INIT_POSITION)
  // Moving onto the card itself (e.g. the camera card's video controls)
  // must not count as "moved away".
  const [isContentHovered, setIsContentHovered] = useState(false)

  // The element `triggerPosition` currently reflects. Adjusted synchronously
  // during render (React's pattern for "derived state that follows a prop")
  // rather than in an effect — an effect runs after paint, so the browser
  // would show the new content at the old position for a frame first.
  const [positionedElementId, setPositionedElementId] = useState(elementId)
  if (
    isHovering &&
    elementId !== positionedElementId &&
    isPopupOpen(triggerPosition)
  ) {
    setPositionedElementId(elementId)
    const clientRect = getClientRectByKind(elementId, kind)
    if (clientRect) setTriggerPosition(clientRect)
  }

  useEffect(() => {
    // The synchronous adjustment above already covers repositioning while a
    // card is open; this dwell only applies to the first open from closed.
    if (!isHovering || isPopupOpen(triggerPosition)) return

    openTimeoutRef.current = setTimeout(() => {
      const clientRect = getClientRectByKind(elementIdRef.current, kind)
      if (clientRect) {
        setTriggerPosition(clientRect)
        setPositionedElementId(elementIdRef.current)
      }
    }, HOVER_OPEN_DELAY_MS)
    return () => clearTimeout(openTimeoutRef.current)
  }, [isHovering, elementId, kind])

  useEffect(() => {
    // Closes once neither the marker nor the card is hovered. The grace
    // period covers the pointer's travel time between the two; re-hovering
    // before it elapses cancels the close via this same effect's cleanup.
    if (!isHovering && !isContentHovered) {
      closeTimeoutRef.current = setTimeout(() => {
        setTriggerPosition(INIT_POSITION)
      }, CLOSE_GRACE_MS)
    }
    return () => clearTimeout(closeTimeoutRef.current)
  }, [isHovering, isContentHovered])

  useEffect(() => {
    // Unconditional: the anchor itself may now be invalid (wrong floor,
    // moved position), so this ignores isContentHovered and any pending
    // grace period.
    if (forceCloseSignal === 0) return
    clearTimeout(openTimeoutRef.current)
    clearTimeout(closeTimeoutRef.current)
    setTriggerPosition(INIT_POSITION)
    setIsContentHovered(false)
  }, [forceCloseSignal])

  useEffect(() => {
    if (
      forceOpenSignal === 0 ||
      forceOpenSignal === lastForceOpenSignalRef.current ||
      !elementId
    ) {
      return
    }
    lastForceOpenSignalRef.current = forceOpenSignal
    clearTimeout(openTimeoutRef.current)
    clearTimeout(closeTimeoutRef.current)
    const clientRect = getClientRectByKind(elementId, kind)
    if (clientRect) {
      setTriggerPosition(clientRect)
      setPositionedElementId(elementId)
    }
  }, [forceOpenSignal, elementId, kind])

  const onPopoverClose = () => {
    if (!screenfull.isFullscreen) {
      setTriggerPosition(INIT_POSITION)
    }
  }

  return {
    triggerPosition,
    isOpen: isPopupOpen(triggerPosition),
    onContentMouseEnter: () => setIsContentHovered(true),
    onContentMouseLeave: () => setIsContentHovered(false),
    onPopoverClose,
  }
}
